/* KANON `Raporty` — the page: a header, a tab strip, and the tab's own two columns.
 *
 * The page shell and the ~1600px measure are `shared/ui/canonScreen.module.css`'s, like `/` and
 * `/plans`. The canon's export is drawn full-bleed at 1440 and says nothing about a wider monitor;
 * the app's answer for a v2 screen is settled, and a third answer here would be a third screen
 * behaving differently for no stated reason.
 *
 * ── The header's two squares are the roster's, not the canon's 44px ──────────────────────────
 * `shared/ui/screenHeader.module.css`'s `.ghost` and `.primary`, 36px — the same band the roster and
 * the plan library draw, so a third square size would be a third answer for eight pixels. What holds
 * those TWO screens to the same y is `e2e/header-parity.spec.ts`; it visits `/` and `/plans` only,
 * and this screen is not in it. The parity here is structural — the shared classes over the shared
 * `canonScreen` — and nothing measures it. Owner, 17 September 2026: the canon's 44px `+` and the
 * eyebrow it drew over the title are both withdrawn, so nothing is left in this band that the other
 * two screens do not also have.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

/* The band the title row stands in. A column of one, and `flex: none` is the whole of its job:
 * `canonScreen`'s measure is a flex column and this band must not take a share of its height. */
.header {
  display: flex;
  flex: none;
  flex-direction: column;
}

/* The bar's own box; `shared/ui/TabStrip` owns everything inside it. The strip stands on the page's
 * ground rather than on a card — the canon draws it as a rule under the heading — so the only
 * declaration beside the height is the hairline it sits on. */
.tabBar {
  gap: var(--space-3);
  height: var(--h-report-tabbar);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* The canon's own title step, over the roster's 31px. */
.header .title {
  font-size: var(--fs-h1);
  letter-spacing: var(--ls-display);
}

/* The search field and the filter button, at the right-hand end of the tab row. They belong to the
 * ANKIETY tab and are drawn only on it: a control that narrows a list the trainer is not looking at
 * is a control that appears to do nothing. */
.tabTools {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
}

/* The canon's toolbar field: 240 wide at the `.field` cut — 40 tall at radius 13, against
 * `SearchField`'s own 286 × 34 at radius 10. A DESCENDANT selector for the reason the header's
 * overrides are: `.frame` and a bare `.search` are both single-class and the winner would be
 * whichever chunk the build happened to emit second. */
.tabTools .search {
  width: var(--w-report-search);
  height: var(--h-report-editor-field);
  border-radius: var(--radius-13px);
}

/* ── The tab's panel ─────────────────────────────────────────────────────────────────────────
 * The panel is the box the two columns stand in and the box the page's remaining height is handed
 * to. `min-height: 0` is what makes both of them scroll inside it rather than growing the page — a
 * flex item's default `min-height: auto` refuses to shrink below its content, which deletes the
 * scroller entirely. */
.panel {
  display: flex;
  flex: 1;
  min-height: 0;
}

.columns {
  display: flex;
  flex: 1;
  gap: var(--space-7);
  min-height: 0;
}
